import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useCampaignByIdQuery } from "Api/Queries/Admin/useCampaignByIdQuery";
import { useContainerWidth } from "Hooks/useContainerWidth";
import {
  useAdminCampaignImageQuery,
  useRevokeBlobUrlOnUnmount,
} from "Api/Queries/Admin/useAdminCampaignImageQuery";
import { getCampaignImages } from "Utils/Campaigns/campaignImageMapping";
import { CampaignImageType } from "Infrastructure/Api/Api";
import { CampaignDetails } from "Components/Campaigns/CampaignDetails";
import { CampaignTimeline } from "Components/Campaigns/CampaignTimeline";
import { DonationForm } from "Components/Campaigns/DonationForm";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { calculateCampaignStatus } from "Utils/Campaign/campaignStatusUtils";
import { getStatusMessage } from "Components/Campaigns/campaignStatusUtils";
import { formatDate } from "Utils/DateUtils";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MooneLogo from "Assets/Images/Images/moone-logo.svg";
import { Resources, useResource } from "Translations/Resources";
import { PreviewHero } from "./PreviewHero";
import { PreviewCard } from "./PreviewCard";

const RESOURCES = Resources.Admin.Campaigns.Form;
const CAMPAIGN_RESOURCES = Resources.Campaigns.Campaign.Page;

type FormOverrides = {
  header?: string | null;
  description?: string | null;
  linkName?: string | null;
  linkUrl?: string | null;
  color?: string | null;
  successfulPaymentText?: string | null;
  paymentInstructionsText?: string | null;
  onlyWithMooneText?: string | null;
};

type Props = {
  campaignId?: number;
  variant: "campaign-page" | "thank-you-page";
  formValues?: FormOverrides;
};

export const PreviewSection = ({ campaignId, variant, formValues }: Props) => {
  const { t } = useResource();
  const theme = useTheme();
  const {
    data: campaign,
    isLoading,
    dataUpdatedAt,
  } = useCampaignByIdQuery(campaignId);
  const container = useContainerWidth();

  const isCampaignPage = variant === "campaign-page";

  const { data: apiIconUrl, isFetching: isIconFetching } =
    useAdminCampaignImageQuery({
      campaignId,
      imageType: CampaignImageType.Icon,
      fileName: campaign?.iconFileName,
      cacheKey: dataUpdatedAt,
    });
  const { data: apiLogoUrl, isFetching: isLogoFetching } =
    useAdminCampaignImageQuery({
      campaignId,
      imageType: CampaignImageType.Logo,
      fileName: isCampaignPage ? campaign?.logoFileName : undefined,
      cacheKey: dataUpdatedAt,
    });
  const { data: apiLandingUrl, isFetching: isLandingFetching } =
    useAdminCampaignImageQuery({
      campaignId,
      imageType: CampaignImageType.Landing,
      fileName: isCampaignPage ? campaign?.landingImageFileName : undefined,
      cacheKey: dataUpdatedAt,
    });
  const { data: apiThankYouUrl, isFetching: isThankYouFetching } =
    useAdminCampaignImageQuery({
      campaignId,
      imageType: CampaignImageType.ThankYou,
      fileName: isCampaignPage ? undefined : campaign?.thankYouImageFileName,
      cacheKey: dataUpdatedAt,
    });

  useRevokeBlobUrlOnUnmount(apiIconUrl);
  useRevokeBlobUrlOnUnmount(apiLogoUrl);
  useRevokeBlobUrlOnUnmount(apiLandingUrl);
  useRevokeBlobUrlOnUnmount(apiThankYouUrl);

  if (!campaignId) {
    return null;
  }

  if (isLoading || !campaign) {
    return <CenteredCircularProgress />;
  }

  // Merge form values for live preview — form overrides saved data
  const previewData = {
    ...campaign,
    ...(formValues && {
      header: formValues.header ?? campaign.header,
      description: formValues.description ?? campaign.description,
      linkName: formValues.linkName ?? campaign.linkName,
      linkUrl: formValues.linkUrl ?? campaign.linkUrl,
      color: formValues.color ?? campaign.color,
      successfulPaymentText:
        formValues.successfulPaymentText ?? campaign.successfulPaymentText,
      paymentInstructionsText:
        formValues.paymentInstructionsText ?? campaign.paymentInstructionsText,
      onlyWithMooneText:
        formValues.onlyWithMooneText ?? campaign.onlyWithMooneText,
    }),
  };

  const hardcodedImages = getCampaignImages(campaign.code);
  const campaignLogoSrc =
    apiLogoUrl || (isLogoFetching ? undefined : hardcodedImages?.logo);
  const iconColor = previewData.color ?? theme.palette.primary.main;
  const iconProps = apiIconUrl
    ? ({ iconUrl: apiIconUrl } as const)
    : ({
        icon: isIconFetching ? null : (
          <FavoriteIcon sx={{ fontSize: "42px", color: iconColor }} />
        ),
      } as const);

  const logoHeader = (
    <Box display="flex" alignItems="center" gap={2} p={3} mb={3}>
      <img src={MooneLogo} alt="moone logo" width={143} height={45} />
      {campaignLogoSrc && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 45,
              borderColor: "text.primary",
              opacity: 0.3,
            }}
          />
          <img
            src={campaignLogoSrc}
            alt={`${campaign.name} logo`}
            width={45}
            height={45}
            style={{ objectFit: "contain" }}
          />
        </>
      )}
    </Box>
  );

  if (variant === "campaign-page") {
    const landingImage =
      apiLandingUrl ||
      (isLandingFetching ? undefined : hardcodedImages?.landing);

    const campaignStatus = calculateCampaignStatus(campaign);
    const statusMessage = getStatusMessage(campaign, t);

    return (
      <PreviewCard
        title={t(RESOURCES.PreviewCampaignPage)}
        containerRef={container.ref}
        width={container.width}
      >
        {logoHeader}
        <Paper elevation={0} sx={{ borderRadius: "10px", overflow: "hidden" }}>
          <Stack gap={5} pb={container.isMobile ? 0 : 15}>
            <PreviewHero
              backgroundImage={landingImage}
              {...iconProps}
              isMobile={container.isMobile}
            />
            <Stack
              p={4}
              gap={5}
              maxWidth={500}
              alignItems="center"
              alignSelf="center"
            >
              {statusMessage && (
                <Alert severity={statusMessage.severity} sx={{ width: "100%" }}>
                  <AlertTitle>{statusMessage.title}</AlertTitle>
                  <Typography variant="body2">
                    {statusMessage.message}
                  </Typography>
                </Alert>
              )}
              {campaignStatus === "active" && campaign.endDate && (
                <Chip
                  label={t(CAMPAIGN_RESOURCES.EndsOn, {
                    date: formatDate(campaign.endDate),
                  })}
                  color="info"
                  variant="outlined"
                  sx={{ alignSelf: "center" }}
                />
              )}
              <CampaignDetails
                header={previewData.header}
                description={previewData.description ?? ""}
                linkUrl={previewData.linkUrl}
                linkName={previewData.linkName}
                headerFontSize={container.isMobile ? "1.5rem" : "2rem"}
              />
              <DonationForm
                campaignCode={campaign.code}
                color={previewData.color}
                currencyCode={campaign.currency}
              />
            </Stack>
          </Stack>
        </Paper>
      </PreviewCard>
    );
  }

  const thankYouImage =
    apiThankYouUrl ||
    (isThankYouFetching ? undefined : hardcodedImages?.thankYou);

  return (
    <PreviewCard
      title={t(RESOURCES.PreviewThankYouPage)}
      containerRef={container.ref}
      width={container.width}
    >
      {logoHeader}
      <Paper elevation={0} sx={{ borderRadius: "10px", overflow: "hidden" }}>
        <Stack gap={3} pb={10}>
          <PreviewHero
            backgroundImage={thankYouImage}
            {...iconProps}
            isMobile={container.isMobile}
          />
          <Typography variant="h4" component="p" textAlign="center" px={4}>
            {t(Resources.Common.ThankYou)}!
          </Typography>
          <Divider
            sx={{
              opacity: 0.5,
              width: "85%",
              mx: "auto",
              mt: container.isMobile ? 3 : 5,
              mb: container.isMobile ? 2 : 3,
            }}
          />
          {previewData.successfulPaymentText && (
            <Typography
              px={4}
              py={2}
              fontWeight={600}
              fontSize="1.125rem"
              textAlign="center"
            >
              {previewData.successfulPaymentText}
            </Typography>
          )}
          <Box mx={10}>
            <CampaignTimeline
              campaignName={campaign.name}
              paymentInstructions={previewData.paymentInstructionsText}
              onlyWithMoone={previewData.onlyWithMooneText}
              inactiveColor={iconColor}
            />
          </Box>
        </Stack>
      </Paper>
    </PreviewCard>
  );
};
